import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, DatePicker, Form, Input, Modal, Space, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { documentService } from '@/services/document.service';
import { useMarkDeliveryDelivered } from '@/hooks/useDelivery';

type Props = {
  open: boolean;
  deliveryId: string;
  deliveryNumber: string;
  customerName?: string | null;
  deliveryAddress?: string | null;
  instructions?: string | null;
  onClose: () => void;
};

type DeliveryCompleteForm = {
  delivered_date: dayjs.Dayjs;
  delivered_time: dayjs.Dayjs;
  remarks?: string;
  tracking_number?: string;
  shipping_method?: string;
};

const MAX_FILES = 10;

export default function DeliveryCompletionModal(props: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<DeliveryCompleteForm>();
  const markDelivered = useMarkDeliveryDelivered();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const uploadedUidsRef = useRef<Set<string>>(new Set());
  const [existingProofCount, setExistingProofCount] = useState(0);

  const busy = uploading || markDelivered.isPending;

  const fileList = useMemo(() => files.slice(0, MAX_FILES), [files]);

  useEffect(() => {
    if (!props.open || !props.deliveryId) return;
    let cancelled = false;
    documentService.getDocumentsByEntity('DeliveryNote', props.deliveryId).then((docs) => {
      if (cancelled) return;
      setExistingProofCount(docs.filter((d) => d.document_category === 'ProofOfDelivery').length);
    });
    return () => {
      cancelled = true;
    };
  }, [props.open, props.deliveryId]);

  const reset = () => {
    setFiles([]);
    uploadedUidsRef.current = new Set();
    setExistingProofCount(0);
    form.resetFields();
  };

  const close = (force = false) => {
    if (busy && !force) return;
    reset();
    props.onClose();
  };

  const uploadProofFiles = async (): Promise<void> => {
    if (fileList.length <= 0 && existingProofCount <= 0) {
      throw new Error('At least one proof file is required');
    }
    for (const uploadFile of fileList) {
      if (!uploadFile.originFileObj || uploadedUidsRef.current.has(uploadFile.uid)) {
        continue;
      }
      const file = uploadFile.originFileObj;
      await documentService.uploadDocument(file, {
        document_name: file.name,
        entity_type: 'DeliveryNote',
        entity_id: props.deliveryId,
        document_category: 'ProofOfDelivery',
        document_type: file.type?.startsWith('image/') ? 'Image' : 'Document',
        description: `Proof of delivery for ${props.deliveryNumber}`,
      });
      uploadedUidsRef.current.add(uploadFile.uid);
      setExistingProofCount((n) => n + 1);
    }
  };

  const submit = async (values: DeliveryCompleteForm) => {
    try {
      setUploading(true);
      await uploadProofFiles();
      const deliveredAt = dayjs(values.delivered_date)
        .hour(values.delivered_time.hour())
        .minute(values.delivered_time.minute())
        .second(values.delivered_time.second())
        .millisecond(0)
        .toISOString();
      await markDelivered.mutateAsync({
        id: props.deliveryId,
        body: {
          delivered_at: deliveredAt,
          remarks: values.remarks?.trim() || undefined,
          tracking_number: values.tracking_number?.trim() || undefined,
          shipping_method: values.shipping_method?.trim() || undefined,
        },
      });
      message.success(`Delivery ${props.deliveryNumber} completed with proof`);
      close(true);
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Failed to complete delivery');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      title={`Complete delivery ${props.deliveryNumber}`}
      open={props.open}
      onCancel={() => close()}
      onOk={() => form.submit()}
      okText="Submit completion"
      okButtonProps={{ loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      destroyOnClose
      width={680}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {existingProofCount > 0 ? (
          <Alert
            type="success"
            showIcon
            message={`${existingProofCount} proof-of-delivery document(s) already attached`}
            description="You can submit without uploading again, or add more files."
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Proof of delivery is required"
            description="Upload at least one photo or PDF document before submission."
          />
        )}
        <Typography.Text>
          <strong>Customer:</strong> {props.customerName || '-'}
        </Typography.Text>
        <Typography.Text>
          <strong>Address:</strong> {props.deliveryAddress || '-'}
        </Typography.Text>
        {props.instructions ? (
          <Typography.Text type="secondary">
            <strong>Instructions:</strong> {props.instructions}
          </Typography.Text>
        ) : null}
      </Space>

      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        initialValues={{
          delivered_date: dayjs(),
          delivered_time: dayjs(),
          remarks: '',
          tracking_number: '',
          shipping_method: '',
        }}
        style={{ marginTop: 16 }}
      >
        <Space style={{ width: '100%' }} size={16} align="start" wrap>
          <Form.Item label="Delivered date" name="delivered_date" rules={[{ required: true }]}>
            <DatePicker />
          </Form.Item>
          <Form.Item label="Delivered time" name="delivered_time" rules={[{ required: true }]}>
            <DatePicker picker="time" format="HH:mm:ss" />
          </Form.Item>
        </Space>

        <Form.Item label="Proof files" required={existingProofCount <= 0}>
          <Upload
            multiple
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: next }) => setFiles(next.slice(0, MAX_FILES))}
            accept="image/*,application/pdf"
          >
            <Button icon={<UploadOutlined />} disabled={busy}>
              Select files
            </Button>
          </Upload>
          <Space style={{ marginTop: 8 }}>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => {
                const picked = e.target.files ? Array.from(e.target.files) : [];
                if (picked.length) {
                  const pickedAsUpload = picked.map(
                    (f) =>
                      ({
                        uid: `${f.name}-${f.lastModified}`,
                        name: f.name,
                        status: 'done',
                        originFileObj: f,
                      }) as UploadFile
                  );
                  setFiles((prev) => [...prev, ...pickedAsUpload].slice(0, MAX_FILES));
                }
                e.currentTarget.value = '';
              }}
            />
            <Button icon={<CameraOutlined />} onClick={() => cameraInputRef.current?.click()} disabled={busy}>
              Take picture
            </Button>
            <Typography.Text type="secondary">
              Camera opens on supported mobile browsers.
            </Typography.Text>
          </Space>
        </Form.Item>

        <Form.Item label="Remarks (optional)" name="remarks">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item label="Tracking number (optional)" name="tracking_number">
          <Input />
        </Form.Item>
        <Form.Item label="Shipping method (optional)" name="shipping_method">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
}
